import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProductPlacementStageEnum } from 'src/app/models/enums/ProductEnums';
import { ModuleProduct, StatefulProductEventsENUM } from 'src/app/models/product-models/views/ModuleProduct';
import { AttributePair, Product, ProductEventsENUM } from 'src/app/models/product-models/views/Product';
import { Vector3 } from 'three';
import { MoveEvent, PlacementStageChangedEvent } from '../../interfaces/ProductEvents';
import { EventBusService } from './event-bus.service';

export interface EdgeData {
  edge: AttributePair | undefined;
  center: AttributePair | undefined;
}
export enum EdgeDirectionEnum {
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
  BOTTOM = 'bottom',
}
const EDGING_TOLERANCE = 0.001;
export const EDGE_DIRECTION_STRING = 'wallEdgeDirection';
export const IS_EDGE_ATTRIBUTE_STRING = 'isWallEdgeAttribute';

@Injectable()
export class EdgeControllerService {
  private _subs: Subscription[] = [];

  private _productsWithAttributesLeftEdge: Map<Product, EdgeData> = new Map<Product, EdgeData>();
  private _productsWithAttributesRightEdge: Map<Product, EdgeData> = new Map<Product, EdgeData>();
  private _productsWithAttributesTopEdge: Map<Product, EdgeData> = new Map<Product, EdgeData>();
  private _productsWithAttributesBottomEdge: Map<Product, EdgeData> = new Map<Product, EdgeData>();

  constructor(private eventBusService: EventBusService) {
    this._subscribeForProductAdded();
  }

  private _subscribeForProductAdded() {
    this._subs.push(
      this.eventBusService.productAdded$.subscribe((productAddedEvent) => {
        this._addProductAttributesToMaps(productAddedEvent.product);

        if (productAddedEvent.product instanceof ModuleProduct)
          productAddedEvent.product.addEventListener(
            StatefulProductEventsENUM.PlacementStageChanged,
            (event: PlacementStageChangedEvent) => {
              if (event.data.placementStage === ProductPlacementStageEnum.Placed) {
                this._checkIfProductIsEdging(productAddedEvent.product);
              } else if (
                event.data.placementStage === ProductPlacementStageEnum.Moving &&
                event.data.originOfPlacementStageChange === productAddedEvent.product
              ) {
                /*                 this._applyCenterAttributes(productAddedEvent.product);
                 */
              }
            },
          );
        productAddedEvent.product.addEventListener(ProductEventsENUM.MoveEvent, (event: MoveEvent) => {
          if (!event.data.previewData) {
            if (event.data.originallyMovedProduct === productAddedEvent.product)
              this._applyCenterAttributes(productAddedEvent.product);
            else {
              if (event.data.originallyMovedProduct.isChildMiddleAttributeSettingEnabled()) {
                this._applyCenterAttributes(productAddedEvent.product);
              }
            }
          } else {
            if (
              event.data.previewData.previewParentDatas.find((previewParentData) =>
                previewParentData.product.isChildMiddleAttributeSettingEnabled(),
              ) &&
              event.data.originallyMovedProduct.isChildMiddleAttributeSettingEnabled()
            ) {
              this._applyCenterAttributes(productAddedEvent.product);
            }
          }
        });
      }),
    );
  }

  private _applyCenterAttributes(product: Product) {
    const edgeDataLeft = this._productsWithAttributesLeftEdge.get(product);
    if (edgeDataLeft && edgeDataLeft.center) {
      product.setAttribute(edgeDataLeft.center.attribute, edgeDataLeft.center.attributeValue);
    }

    const edgeDataRight = this._productsWithAttributesRightEdge.get(product);
    if (edgeDataRight && edgeDataRight.center) {
      product.setAttribute(edgeDataRight.center.attribute, edgeDataRight.center.attributeValue);
    }
  }

  private _checkIfProductIsEdging(product: Product) {
    if (!product.wall) return;

    const productBBox = product.getLocalBoundingBox();

    const moduleLocalPosition = new Vector3();
    product.getWorldPosition(moduleLocalPosition);
    product.wall.worldToLocal(moduleLocalPosition);

    const maxRight = Math.abs(product.wall.width / -2 + moduleLocalPosition.x) - productBBox.max.x;
    const maxLeft = product.wall.width / 2 + moduleLocalPosition.x + productBBox.min.x;
    const maxTop = Math.abs(product.wall.height / -2 + moduleLocalPosition.y) - productBBox.max.y;
    const maxBottom = product.wall.height / -2 + moduleLocalPosition.y - productBBox.max.y;

    for (const wallPositionConstraint of product.productBlueprint.movementData.wallPositionConstraints) {
      if (wallPositionConstraint.wallType !== product.wall.wallType) {
        continue;
      }
      if (wallPositionConstraint.leftOffset) {
        const leftConstraint = wallPositionConstraint.leftOffset.direction
          ? product.wall.width - wallPositionConstraint.leftOffset.value
          : wallPositionConstraint.leftOffset.value;

        if (leftConstraint + EDGING_TOLERANCE > maxLeft) {
          const edgeData = this._productsWithAttributesLeftEdge.get(product);
          if (edgeData && edgeData.edge) {
            product.setAttribute(edgeData.edge.attribute, edgeData.edge.attributeValue);
          }
        }
      }

      if (wallPositionConstraint.rightOffset) {
        const rightConstraint = wallPositionConstraint.rightOffset.direction
          ? product.wall.width - wallPositionConstraint.rightOffset.value
          : wallPositionConstraint.rightOffset.value;

        if (rightConstraint + EDGING_TOLERANCE > maxRight) {
          const edgeData = this._productsWithAttributesRightEdge.get(product);
          if (edgeData && edgeData.edge) {
            product.setAttribute(edgeData.edge.attribute, edgeData.edge.attributeValue);
          }
        }
      }

      if (wallPositionConstraint.topOffset) {
        const topConstraint = wallPositionConstraint.topOffset.direction
          ? product.wall.width - wallPositionConstraint.topOffset.value
          : wallPositionConstraint.topOffset.value;

        if (topConstraint + EDGING_TOLERANCE > maxTop) {
          const edgeData = this._productsWithAttributesTopEdge.get(product);
          if (edgeData && edgeData.edge) {
            product.setAttribute(edgeData.edge.attribute, edgeData.edge.attributeValue);
          }
        }
      }

      if (wallPositionConstraint.bottomOffset) {
        const bottomConstraint = wallPositionConstraint.bottomOffset.direction
          ? product.wall.width - wallPositionConstraint.bottomOffset.value
          : wallPositionConstraint.bottomOffset.value;

        if (bottomConstraint + EDGING_TOLERANCE > maxBottom) {
          const edgeData = this._productsWithAttributesBottomEdge.get(product);
          if (edgeData && edgeData.edge) {
            product.setAttribute(edgeData.edge.attribute, edgeData.edge.attributeValue);
          }
        }
      }
    }
  }

  private _addProductAttributesToMaps(product: Product) {
    const productBlueprint = product.productBlueprint;

    for (const attribute of productBlueprint.attributes) {
      const leftEdgeDataToAdd: EdgeData = { edge: undefined, center: undefined };
      const rightEdgeDataToAdd: EdgeData = { edge: undefined, center: undefined };
      const bottomEdgeDataToAdd: EdgeData = { edge: undefined, center: undefined };
      const topEdgeDataToAdd: EdgeData = { edge: undefined, center: undefined };

      for (const attributeValue of attribute.attributeValues) {
        if (
          !attribute.builderData ||
          !attributeValue.builderData ||
          attribute.builderData[EDGE_DIRECTION_STRING] === undefined
        )
          continue;

        if (attribute.builderData[EDGE_DIRECTION_STRING] === EdgeDirectionEnum.LEFT) {
          if (attributeValue.builderData[IS_EDGE_ATTRIBUTE_STRING]) {
            leftEdgeDataToAdd.edge = { attribute: attribute, attributeValue: attributeValue };
          } else {
            leftEdgeDataToAdd.center = { attribute: attribute, attributeValue: attributeValue };
          }
        } else if (attribute.builderData[EDGE_DIRECTION_STRING] === EdgeDirectionEnum.RIGHT) {
          if (attributeValue.builderData[IS_EDGE_ATTRIBUTE_STRING]) {
            rightEdgeDataToAdd.edge = { attribute: attribute, attributeValue: attributeValue };
          } else {
            rightEdgeDataToAdd.center = { attribute: attribute, attributeValue: attributeValue };
          }
        } else if (attribute.builderData[EDGE_DIRECTION_STRING] === EdgeDirectionEnum.TOP) {
          if (attributeValue.builderData[IS_EDGE_ATTRIBUTE_STRING]) {
            topEdgeDataToAdd.edge = { attribute: attribute, attributeValue: attributeValue };
          } else {
            topEdgeDataToAdd.center = { attribute: attribute, attributeValue: attributeValue };
          }
        } else if (attribute.builderData[EDGE_DIRECTION_STRING] === EdgeDirectionEnum.BOTTOM) {
          if (attributeValue.builderData[IS_EDGE_ATTRIBUTE_STRING]) {
            bottomEdgeDataToAdd.edge = { attribute: attribute, attributeValue: attributeValue };
          } else {
            bottomEdgeDataToAdd.center = { attribute: attribute, attributeValue: attributeValue };
          }
        }
      }
      if (leftEdgeDataToAdd.center || leftEdgeDataToAdd.edge)
        this._productsWithAttributesLeftEdge.set(product, leftEdgeDataToAdd);
      if (rightEdgeDataToAdd.center || rightEdgeDataToAdd.edge)
        this._productsWithAttributesRightEdge.set(product, rightEdgeDataToAdd);
      if (topEdgeDataToAdd.center || topEdgeDataToAdd.edge)
        this._productsWithAttributesTopEdge.set(product, topEdgeDataToAdd);
      if (bottomEdgeDataToAdd.center || bottomEdgeDataToAdd.edge)
        this._productsWithAttributesBottomEdge.set(product, bottomEdgeDataToAdd);
    }
  }

  public dispose() {
    for (const sub of this._subs) {
      sub.unsubscribe();
    }
  }
}
